import React, { useEffect, useState } from 'react';
import * as adminApi from '../../../api/adminApi';
import { S } from './adminPageStyles';

/** Data coverage acceptance (SRS #167): the five completeness claims measured against the corpus,
 *  with the evidence behind each verdict and a markdown export for the sign-off pack. */
export const CoveragePanel: React.FC<{ token: string }> = ({ token }) => {
  const [rep, setRep] = useState<adminApi.CoverageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    adminApi.getDataCoverage(token).then(setRep).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [token]);

  const chip = (v: string): React.CSSProperties => ({
    ...S.chip, fontWeight: 700,
    background: v === 'pass' ? 'rgba(2,122,72,0.10)' : v === 'fail' ? 'rgba(240,68,56,0.10)' : 'rgba(247,144,9,0.14)',
    color: v === 'pass' ? 'var(--ds-success)' : v === 'fail' ? 'var(--ds-danger)' : 'var(--ds-warning)',
  });

  if (error) return <div style={S.error}>{error}</div>;
  if (!rep) return <div style={S.muted}>Measuring the corpus…</div>;

  const evidence = (c: adminApi.CoverageCriterion): React.ReactNode => {
    if ('missing' in c) return (
      <>
        <div>{c.covered} of 50 states have at least one instrument{c.missing.length > 0 && <> — missing: <b>{c.missing.join(', ')}</b></>}.</div>
        <div>LegiScan pipeline {c.pipeline_enabled ? 'enabled' : <b>disabled</b>}; last successful sync {c.last_successful_sync_at ? `${c.lag_hours} h ago` : 'never'} (budget {c.lag_budget_hours} h).</div>
      </>
    );
    if ('lowest' in c) return (
      <>
        <div>{c.total} federal instruments: {c.scored} scored, {c.unscored} unscored, {c.relevant} at or above {c.floor} ({c.relevant_pct_of_scored} % of scored).</div>
        {c.lowest.length > 0 && <ul style={{ margin: '4px 0 0 16px' }}>{c.lowest.slice(0, 5).map((l: any) => <li key={l.id}>{l.title} — {l.score}</li>)}</ul>}
      </>
    );
    if ('ai_act_count' in c) return <div>{c.eu_total} EU instruments; AI Act matches: {c.ai_act_count}; implementing / delegated / guidelines: {c.implementing_count}.</div>;
    if ('by_country' in c) return <div>{c.count} of {c.required} required: {c.by_country.slice(0, 15).map((x: any) => `${x.country} (${x.instruments})`).join(', ')}{c.by_country.length > 15 ? ', …' : ''}</div>;
    return <div>{c.count} instruments; latest action {c.latest_action ?? '—'}; pipeline {c.pipeline_enabled ? 'enabled' : 'disabled'}.</div>;
  };

  return (
    <div data-testid="coverage">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 12px', flexWrap: 'wrap' }}>
        <span style={chip(rep.overall)}>Overall: {rep.overall.toUpperCase()}</span>
        <span style={S.muted}>{rep.instruments.toLocaleString()} instruments · measured {new Date(rep.generated_at).toLocaleString()}</span>
        <button style={{ ...S.btn, marginLeft: 'auto' }} onClick={() => adminApi.downloadDataCoverageMarkdown(token).catch((e) => setError(e.message))}>Export for sign-off (.md)</button>
      </div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>Claim</th><th style={S.th}>Verdict</th><th style={S.th}>Finding</th></tr></thead>
        <tbody>
          {rep.criteria.map((c, i) => (
            <React.Fragment key={c.claim}>
              <tr data-testid={`claim-${i}`} onClick={() => setOpen(open === i ? null : i)} style={{ cursor: 'pointer' }}>
                <td style={S.td}>{c.claim}</td>
                <td style={S.td}><span style={chip(c.verdict)}>{c.verdict}</span></td>
                <td style={S.td}>{c.note}</td>
              </tr>
              {open === i && <tr><td style={{ ...S.td, background: 'var(--ds-surface-2)' }} colSpan={3}>{evidence(c)}</td></tr>}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {rep.needs_from_kn.length > 0 && (
        <div style={{ ...S.error, marginTop: 12, background: 'rgba(247,144,9,0.10)', color: 'var(--ds-text)' }}>
          <b>Before the data SLA is signed:</b>
          <ul style={{ margin: '6px 0 0 16px' }}>{rep.needs_from_kn.map((n) => <li key={n}>{n}</li>)}</ul>
        </div>
      )}
    </div>
  );
};
